import { useState, useEffect, useRef } from 'react';
import { EngineEvaluation, buildEvidencePackage, EvidencePackage, detectOpening } from '@chess-platform/shared';
import { engineManager } from '../engine/engineManager';
import { reviewEngine } from '../engine/reviewEngine';
import { audioManager } from '../../../utils/audioManager';

/**
 * usePlayEngine: Proporciona análisis en tiempo real para la PARTIDA.
 * Siguiendo la petición del usuario: SIMPLIFICADO.
 * - Detecta aperturas al momento.
 * - Solo analiza a fondo la ÚLTIMA jugada para dar un comentario rápido.
 * - No colorea todo el historial para evitar "ruido" y lentitud.
 */
export function usePlayEngine(fen: string, history: string[], uciHistory: string[], fenHistory: string[], isReady: boolean, active: boolean = true, playerColor: 'w' | 'b' = 'w') {
    const [evaluation, setEvaluation] = useState<EngineEvaluation | null>(null);
    const [reviewHistory, setReviewHistory] = useState<EvidencePackage[]>([]);
    const lastProcessedPly = useRef(-1);
    const evaluationCache = useRef<Record<string, EngineEvaluation | undefined>>({});
    const [isCalculating, setIsCalculating] = useState(false);

    // 1. Análisis en vivo (M1) para el HUD (Puntuación + Mejor jugada sugerida)
    useEffect(() => {
        if (!active || !isReady) return;
        const cleanup = engineManager.subscribe((ev) => setEvaluation(ev));
        engineManager.analyze(fen, 'depth 15', history.length);
        return cleanup;
    }, [fen, history.length, isReady, active]);

    // 2. Lógica simplificada de comentarios y apertura
    useEffect(() => {
        // La detección de apertura NO necesita que el motor esté listo
        if (!active || history.length === 0) {
            if (!active) setIsCalculating(false);
            return;
        }

        const i = history.length - 1;
        if (lastProcessedPly.current === i) return;

        // Normalización de uciHistory para detección (usando el uciHistory actual o un fallback)
        const currentUciHistory = uciHistory || [];
        const normalizedUciHistory = currentUciHistory.slice(0, i + 1).map(m => m.toLowerCase());
        const opening = detectOpening(normalizedUciHistory);

        // Si el motor no está listo, solo podemos dar info de apertura si existe
        if (!isReady) {
            if (opening) {
                const bookEvidence = buildEvidencePackage({
                    ply: i,
                    fenAfter: fen,
                    fenBefore: i === 0 ? 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' : (fenHistory[i] || 'start'),
                    moveUci: (currentUciHistory[i] || '').toLowerCase(),
                    moveSan: history[i],
                    evalBeforePawns: 0.3, evalAfterPawns: 0.3,
                    bestMoveUci: (currentUciHistory[i] || '').toLowerCase(),
                    engineDepth: 1,
                    uciHistory: currentUciHistory.slice(0, i),
                    playerColor
                });
                setReviewHistory(prev => {
                    const next = [...prev];
                    next[i] = bookEvidence;
                    return next;
                });
            }
            return;
        }

        lastProcessedPly.current = i;
        setIsCalculating(true);

        const runQuickReview = async () => {
            try {
                const moveUci = (uciHistory[i] || '').toLowerCase();
                const fenBefore = i === 0
                    ? 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
                    : (fenHistory[i] && fenHistory[i] !== 'start' ? fenHistory[i] : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
                const fenAfter = fen;

                let eBefore = evaluationCache.current[fenBefore];
                if (!eBefore) {
                    eBefore = (await reviewEngine.reviewPosition(fenBefore)) || undefined;
                    if (eBefore) evaluationCache.current[fenBefore] = eBefore;
                }

                let eAfter = (await reviewEngine.reviewPosition(fenAfter)) || undefined;
                if (eAfter) evaluationCache.current[fenAfter] = eAfter;

                const evidence = buildEvidencePackage({
                    ply: i, fenAfter, fenBefore, moveUci, moveSan: history[i],
                    evalBeforePawns: eBefore?.score ?? 0,
                    evalAfterPawns: eAfter?.score ?? (eBefore?.score ?? 0),
                    isMate: eAfter?.isMate,
                    mateMoves: eAfter?.mateMoves,
                    mateBefore: eBefore?.mateMoves,
                    bestMoveUci: eBefore?.bestMove || '',
                    engineDepth: eAfter?.depth ?? 1,
                    uciHistory: uciHistory.slice(0, i),
                    playerColor
                });

                setReviewHistory(prev => {
                    const next = [...prev];
                    next[i] = evidence;
                    return next;
                });

                // Play classification sound only for the human player's moves
                const humanMoved = evidence.sideThatMoved === playerColor;
                if (humanMoved) {
                    const label = evidence.quality.label;
                    if (label === 'blunder' || label === 'mistake') {
                        audioManager.play('blunder');
                    } else if (label === 'brilliant' || label === 'best') {
                        audioManager.play('brilliant');
                    }
                }
            } catch (err) {
                console.error('[M3-Quick] Error:', err);
            } finally {
                if (lastProcessedPly.current === i) {
                    setIsCalculating(false);
                }
            }
        };

        runQuickReview();
    }, [history.length, isReady, active, uciHistory.length, playerColor]);

    const currentOpening = detectOpening(uciHistory);

    return {
        evaluation,
        reviewHistory,
        isCalculating,
        currentOpening,
        lastReview: reviewHistory[history.length - 1] || null
    };
}
